/**
 * StatusChip — Status-Badges fuer CDR-Verifikation und Ban-Status (Story 0.3, AC-3)
 *
 * Referenz: claude-design-import/hotspot-detail.html (`chips()`, `.pill` +
 * `.status-chip` + `.tip`).
 *
 * a11y-Auflage (Form-/Muster-Redundanz): Jeder Chip traegt IMMER Icon UND
 * Text — der Zustand ist nie ausschliesslich ueber die Farbe kodiert.
 * Zusaetzlich liefert jeder Chip einen Standard-Tooltip, der per
 * aria-describedby verknuepft ist (Tastatur-fokussierbar via tabIndex 0).
 */
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle,
  CheckCircle2,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from 'lucide-react'
import { Tooltip } from './Tooltip'
import './ui.css'

export type CdrStatus = 'valid' | 'failed'
export type BanStatus = 'active' | 'clean'

/**
 * Qualitäts-Einstufung (Story 5.5 Speedtest-Monitor u.a.):
 * good → success-Ton, warning → warning-Ton, poor → danger-Ton.
 * Label + Tooltip liefert der Aufrufer aus seinem eigenen i18n-Namespace
 * (deshalb hier Pflichtfelder — kein Eintrag im geteilten `ui`-Namespace).
 */
export type QualityStatus = 'good' | 'warning' | 'poor'

/** Restdauer eines aktiven Bans. Tage/Stunden werden lokalisiert zusammengesetzt. */
export type BanRemaining = {
  days?: number
  hours?: number
}

type CommonProps = {
  /** Ueberschreibt den Standard-Tooltip-Text. */
  tooltip?: string
  /** Ueberschreibt das Standard-Label. */
  label?: string
  className?: string
  'data-testid'?: string
}

export type StatusChipProps = CommonProps &
  (
    | { kind: 'cdr'; status: CdrStatus; remaining?: never }
    | { kind: 'ban'; status: BanStatus; remaining?: BanRemaining | null }
    | {
        kind: 'quality'
        status: QualityStatus
        /** Pflicht: Label aus dem Feature-Namespace des Aufrufers. */
        label: string
        /** Pflicht: Tooltip aus dem Feature-Namespace des Aufrufers. */
        tooltip: string
        remaining?: never
      }
  )

type Tone = 'success' | 'warning' | 'danger' | 'neutral'

const TONE_CLASS: Record<Tone, string> = {
  success: 'pill-success',
  warning: 'pill-warning',
  danger: 'pill-danger',
  neutral: '',
}

export function StatusChip(props: StatusChipProps) {
  const { kind, status, tooltip, label, className = '', 'data-testid': testId } = props
  const { t } = useTranslation('ui')

  /**
   * Restdauer als Text, z. B. „2 T 14 h“ (DE) / „2d 14h“ (EN).
   * Nur positive Anteile werden gerendert; 0/negativ → „läuft aus“.
   */
  function formatRemaining(remaining: BanRemaining): string | null {
    const days = Math.floor(remaining.days ?? 0)
    const hours = Math.floor(remaining.hours ?? 0)
    const parts: string[] = []
    if (days > 0) parts.push(t('statusChip.duration.days', { value: days }))
    if (hours > 0) parts.push(t('statusChip.duration.hours', { value: hours }))
    if (parts.length === 0) return t('statusChip.duration.expired')
    return parts.join(' ')
  }

  let tone: Tone
  let icon: React.ReactNode
  let defaultLabel: string
  let defaultTooltip: string

  if (kind === 'quality') {
    tone = status === 'good' ? 'success' : status === 'warning' ? 'warning' : 'danger'
    icon =
      status === 'good' ? (
        <CheckCircle2 aria-hidden="true" size={13} className="ui-status-chip__icon" />
      ) : status === 'warning' ? (
        <AlertTriangle aria-hidden="true" size={13} className="ui-status-chip__icon" />
      ) : (
        <XCircle aria-hidden="true" size={13} className="ui-status-chip__icon" />
      )
    // label/tooltip sind bei kind='quality' Pflichtprops (siehe Typ-Union).
    defaultLabel = props.label
    defaultTooltip = props.tooltip
  } else if (kind === 'cdr') {
    const valid = status === 'valid'
    tone = valid ? 'success' : 'warning'
    icon = valid ? (
      <CheckCircle2 aria-hidden="true" size={13} className="ui-status-chip__icon" />
    ) : (
      <AlertTriangle aria-hidden="true" size={13} className="ui-status-chip__icon" />
    )
    defaultLabel = t(`statusChip.cdr.${valid ? 'valid' : 'failed'}.label`)
    defaultTooltip = t(`statusChip.cdr.${valid ? 'valid' : 'failed'}.tooltip`)
  } else {
    const active = status === 'active'
    tone = active ? 'danger' : 'neutral'
    icon = active ? (
      <ShieldAlert aria-hidden="true" size={13} className="ui-status-chip__icon" />
    ) : (
      <ShieldCheck aria-hidden="true" size={13} className="ui-status-chip__icon" />
    )
    const remainingText =
      active && props.remaining ? formatRemaining(props.remaining) : null
    defaultLabel = active
      ? remainingText
        ? t('statusChip.ban.active.labelWithRemaining', { remaining: remainingText })
        : t('statusChip.ban.active.label')
      : t('statusChip.ban.clean.label')
    defaultTooltip = active
      ? remainingText
        ? t('statusChip.ban.active.tooltipWithRemaining', { remaining: remainingText })
        : t('statusChip.ban.active.tooltip')
      : t('statusChip.ban.clean.tooltip')
  }

  const resolvedLabel = label ?? defaultLabel
  const resolvedTooltip = tooltip ?? defaultTooltip

  return (
    <Tooltip text={resolvedTooltip}>
      {(tipId) => (
        <span
          className={`pill ui-status-chip ${TONE_CLASS[tone]} ${className}`.trim()}
          data-testid={testId ?? `status-chip-${kind}`}
          data-status={status}
          data-tone={tone}
          tabIndex={0}
          aria-describedby={tipId}
        >
          {icon}
          {resolvedLabel}
        </span>
      )}
    </Tooltip>
  )
}
